import { BoardAdTypeOption, BoardAdTypeOptions } from '@/_shared/types'
import { BoardDialog } from '@/components/BoardDialog.tsx'
import { DeleteBoardDialog } from '@/components/DeleteBoard.tsx'
import { Stack, Text } from '@/components/custom-components'
import { FeatureTabs } from '@/components/custom-components/FeatureTabs'
import { Loader } from '@/components/custom-components/Loader'
import { ErrorDisplay } from '@/components/error.tsx'
import { Button } from '@/components/shadcn/button'
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from '@/components/shadcn/select'
import { LandingAdCard, LandingFeedAdCard } from '@/components/templates/LandingAdCard'
import { useCopyToClipboard } from '@/hooks/useCopyToClipboard'
import showToastNotification from '@/hooks/useShowToast'
import { trpc } from '@/utils/trpc.ts'
import { Link as LinkIcon, Pencil, Trash } from '@mynaui/icons-react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { useInView } from 'react-intersection-observer'
const LIMIT = 10

type SearchParams = {
  sideBarOpen?: boolean
}

export const Route = createFileRoute('/feeds/boards/$boardID/ad-landers')({
  component: BoardLanders,
  validateSearch: (search: Record<string, unknown>): SearchParams => {
    const sideBarOpen = search?.sideBarOpen as boolean

    return {
      sideBarOpen,
    }
  },
})

function BoardLanders() {
  const navigate = useNavigate()

  const { boardID } = Route.useParams()
  const [editBoardDialogOpen, setEditBoardDialogOpen] = useState<boolean>(false)

  const [deleteBoardDialogOpen, setDeleteBoardDialogOpen] = useState<boolean>(false)
  const [, copyBoardAdToClipboard] = useCopyToClipboard()
  const {
    data: boardDetails,
    isLoading,
    error,
  } = trpc.getUserBoardById.useQuery(
    {
      boardId: boardID,
    },
    {
      refetchOnMount: true,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    },
  )

  const {
    data: boardLanders,
    isLoading: adsLoading,
    isError: adsError,
    isFetchingNextPage: isFetchingNextLandersPage,
    fetchNextPage: fetchNextLandersPage,
    hasNextPage: hasNextLandersPage,
    isRefetching: landersRefetching,
  } = trpc.getBoardLanderInspirationPaginated.useInfiniteQuery(
    { limit: LIMIT, boardId: boardID },
    {
      getNextPageParam: (lastPage) => lastPage.nextCursor,
      refetchOnMount: true,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    },
  )

  const { ref: scrollRef, inView } = useInView({
    threshold: 0,
    trackVisibility: true,
    delay: 100,
  })

  // Fetch next page when reaching the bottom
  useEffect(() => {
    if (inView && hasNextLandersPage && !isFetchingNextLandersPage) {
      if (
        hasNextLandersPage &&
        !isFetchingNextLandersPage &&
        (boardLanders?.pages?.at(-1)?.results?.length ?? 0) >= LIMIT
      ) {
        fetchNextLandersPage()
      }
    }
  }, [inView, hasNextLandersPage, isFetchingNextLandersPage, fetchNextLandersPage])

  /// HERE IS THE SECTION FOR THE TEMPLATES

  const {
    data: boardLandersTemp,
    isLoading: adsLoadingTemp,
    isError: adsErrorTemp,
    isFetchingNextPage: isFetchingNextLandersTempPage,
    fetchNextPage: fetchNextLandersTempPage,
    hasNextPage: hasNextLandersTempPage,
    isRefetching: landersTempRefetching,
  } = trpc.getBoardLanderTemplatesPaginated.useInfiniteQuery(
    { limit: LIMIT, boardId: boardID },
    {
      getNextPageParam: (lastPage) => lastPage.nextCursor,
      refetchOnMount: true,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    },
  )

  // Fetch next page when reaching the bottom
  useEffect(() => {
    if (inView && hasNextLandersTempPage && !isFetchingNextLandersTempPage) {
      if (
        hasNextLandersTempPage &&
        !isFetchingNextLandersTempPage &&
        (boardLandersTemp?.pages?.at(-1)?.results?.length ?? 0) >= LIMIT
      ) {
        fetchNextLandersTempPage()
      }
    }
  }, [inView, hasNextLandersTempPage, isFetchingNextLandersTempPage, fetchNextLandersTempPage])

  if (adsErrorTemp || error || adsError || error) {
    return (
      <div className={'px-0'}>
        <ErrorDisplay />
      </div>
    )
  }

  if (
    !boardLandersTemp ||
    adsLoadingTemp ||
    landersTempRefetching ||
    isLoading ||
    !boardLanders ||
    adsLoading ||
    landersRefetching ||
    isLoading
  ) {
    return (
      <div className="flex justify-center items-center w-full h-screen m-auto">
        <Loader />
      </div>
    )
  }

  const allSavedAds = boardLanders?.pages.flatMap((page) => page.results) || []

  const noAdsAvailable = allSavedAds.length === 0

  const totalAdsCount = boardLanders.pages.length > 0 ? boardLanders.pages[0].results.length : 0

  const allSaveLanderTemp = boardLandersTemp?.pages.flatMap((page) => page.results) || []

  const noLanderTempAvailable = allSaveLanderTemp.length === 0

  const totalLanderTempCount = boardLandersTemp.pages.length > 0 ? boardLandersTemp.pages[0].results.length : 0

  /// HERE IS THE SECTION FOR THE TEMPLATES

  return (
    <>
      {editBoardDialogOpen && boardDetails && (
        <BoardDialog
          data={{
            name: boardDetails.name,
            id: boardDetails.id,
            description: boardDetails.description || '',
          }}
          open={editBoardDialogOpen}
          onOpenChange={() => setEditBoardDialogOpen(false)}
        />
      )}
      {deleteBoardDialogOpen && boardDetails && (
        <DeleteBoardDialog
          data={{
            name: boardDetails.name,
            id: boardDetails.id,
            description: boardDetails.description || '',
          }}
          open={deleteBoardDialogOpen}
          onOpenChange={() => setDeleteBoardDialogOpen(false)}
        />
      )}
      <Stack className="gap-3 lg:gap-8">
        <Stack className="gap-3 lg:gap-8">
          <Stack className="gap-3 lg:gap-2">
            {boardDetails ? (
              <div className={'flex justify-between gap-5 items-start'}>
                <Stack className="gap-2">
                  <Text weight="semibold" size={'xxl'}>
                    {boardDetails?.name}
                  </Text>
                  <div className={`flex gap-1 items-center`}>
                    <Text className="text-thememutedforeground w-fit" size={'base'} weight={'normal'}>
                      {boardDetails?.description}
                    </Text>
                  </div>
                </Stack>
                <Stack className="gap-3 items-end">
                  <div className="flex items-center justify-end gap-6">
                    <FeatureTabs
                      tabItems={[
                        {
                          name: 'Ads',
                          link: `/feeds/boards/${boardID}/ads`,
                        },
                        {
                          name: 'Landers',
                          link: `/feeds/boards/${boardID}/ad-landers`,
                        },
                        {
                          name: 'Emails',
                          link: `/feeds/boards/${boardID}/emails`,
                        },
                      ]}
                    />
                    <Select
                      value={BoardAdTypeOptions.ALL_SAVED}
                      onValueChange={(value: BoardAdTypeOption) => {
                        if (value === BoardAdTypeOptions.ONLY_TEMPLATES) {
                          navigate({
                            to: `/feeds/boards/$boardID/ad-lander-templates`,
                            params: { boardID },
                          })
                        }
                      }}
                    >
                      <SelectTrigger className="w-33 h-8 max-h-8">
                        <SelectValue placeholder="Ad Type" className="rounded-md" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectGroup>
                          {Object.values(BoardAdTypeOptions).map((item) => (
                            <SelectItem key={item} value={item} className="py-1.5">
                              {item}
                            </SelectItem>
                          ))}
                        </SelectGroup>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex gap-1.5 h-8">
                    <Button
                      onClick={() => {
                        copyBoardAdToClipboard(`${window.location.origin}/feeds/boards/${boardDetails?.id}/ads`)
                        showToastNotification('success', {
                          message: 'Copied!',
                        })
                      }}
                      className="hidden lg:flex gap-1.5"
                      variant={'outline'}
                      size={'sm'}
                    >
                      <LinkIcon className="w-5 h-5" />
                      Share
                    </Button>
                    <Button
                      onClick={() => {
                        copyBoardAdToClipboard(`${window.location.origin}/feeds/boards/${boardDetails?.id}/ads`)
                        showToastNotification('success', {
                          message: 'Copied!',
                        })
                      }}
                      className="lg:hidden flex gap-1.5"
                      variant={'outline'}
                      size={'iconMid'}
                    >
                      <LinkIcon className="w-5 h-5" />
                    </Button>
                    <Button
                      onClick={() => setEditBoardDialogOpen(true)}
                      className="gap-1.5"
                      variant={'outline'}
                      size={'iconMid'}
                    >
                      <Pencil className="w-5 h-5" />
                    </Button>
                    <Button
                      onClick={() => setDeleteBoardDialogOpen(true)}
                      className="gap-1.5"
                      variant={'outline'}
                      size={'iconMid'}
                    >
                      <Trash className="w-5 h-5" />
                    </Button>
                  </div>
                  <Text
                    className="text-thememutedforeground w-fit text-nowrap hidden lg:flex"
                    size={'base'}
                    weight={'normal'}
                  >
                    {totalAdsCount + totalLanderTempCount}
                    {totalAdsCount + totalLanderTempCount == 1 ? ' Item' : ' Items'}
                  </Text>
                </Stack>
              </div>
            ) : null}
          </Stack>
        </Stack>

        {isLoading ? (
          <div className={'flex justify-center items-center w-full h-screen m-auto'}>
            <Loader />
          </div>
        ) : error ? (
          <ErrorDisplay />
        ) : (
          <div className={''}>
            {boardDetails && (
              <>
                {noAdsAvailable && noLanderTempAvailable ? (
                  <div className={'lg:h-96 flex justify-center items-center'}>
                    <p className="text-center leading-5">
                      No landers added to this collection.{' '}
                      {/* <Link
                        to="/feeds/boards/$boardID/ad-lander-templates"
                        className="text-blue-500 underline"
                        params={{ boardID }}
                      >
                        View Your Template
                      </Link>{" "}
                      instead */}
                    </p>
                  </div>
                ) : (
                  <div className={'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2.5'}>
                    {allSavedAds.map((card) => (
                      <LandingFeedAdCard
                        key={card.landerId}
                        adData={{
                          brandId: card.brandId,
                          brandName: card.brandName,
                          brandImage: card.brandImage,
                          desktopScreenshot: card.desktopScreenshot,
                          landerId: card.landerId,
                          landerUrl: card.landerUrl ?? '',
                          mobileScreenshot: card.mobileScreenshot,
                          isSaved: card.isSaved,
                        }}
                      />
                    ))}
                    {allSaveLanderTemp.map((card) => (
                      <LandingAdCard
                        key={card['LP ID']}
                        adData={{
                          Screenshot: card['Landing Page Screenshot'],
                          ID: card['LP ID'],
                          created: card.Created,
                          category: card.Category,
                          isSaved: card.isSaved,
                          imageUrl: card.imageUrl,
                        }}
                        showTemplateButton
                        type={'landing-page'}
                      />
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        )}
        {/* Bottom scroll loader */}
        {hasNextLandersPage && (
          <div ref={scrollRef} className="flex justify-center w-full min-h-14 h-14">
            {isFetchingNextLandersPage && <Loader />}
          </div>
        )}
      </Stack>
    </>
  )
}
